// Admin Passcode Modal with SHA-256 Web Crypto Hashing per ORIGINAL_REQUEST § R2 & PROJECT.md
import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../common';
import { PinPad } from './PinPad';
import { Lock, KeyRound, CheckCircle2 } from 'lucide-react';
import { getSetting, saveSetting } from '../../db';

export const DEFAULT_PIN_HASH = '03ac674216f3e15c761ee1a5e255f067953623c8b388b4459e13f978d7c846f4'; // SHA-256 of "1234"
export const PIN_SETTING_KEY = 'admin_pin_hash';

// Standard SHA-256 implementation in pure JS (RFC 6234 compliant)
// Ensures 100% consistent SHA-256 hashes even on plain HTTP IP origins where crypto.subtle is disabled
function sha256PureJs(ascii: string): string {
  function rightRotate(value: number, amount: number) {
    return (value >>> amount) | (value << (32 - amount));
  }
  const mathPow = Math.pow;
  const maxWord = mathPow(2, 32);
  let i: number, j: number;
  let result = '';
  const words: number[] = [];
  const asciiBitLength = ascii.length * 8;
  let hash: number[] = [];
  const k: number[] = [];
  let primeCounter = 0;
  const isComposite: Record<number, boolean> = {};

  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (!isComposite[candidate]) {
      for (i = 0; i < 313; i += candidate) {
        isComposite[i] = true;
      }
      hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
      k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
    }
  }

  ascii += '\x80';
  while (ascii.length % 64 - 56) ascii += '\x00';
  for (i = 0; i < ascii.length; i++) {
    j = ascii.charCodeAt(i);
    words[i >> 2] |= j << ((3 - i) % 4) * 8;
  }
  words[words.length] = (asciiBitLength / maxWord) | 0;
  words[words.length] = asciiBitLength;

  for (j = 0; j < words.length; ) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash.slice(0, 8);
    for (i = 0; i < 64; i++) {
      const i2 = i + j;
      const w15 = w[i - 15],
        w2 = w[i - 2];
      const a = hash[0],
        e = hash[4];
      const temp1 =
        hash[7] +
        (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) +
        ((e & hash[5]) ^ (~e & hash[6])) +
        k[i] +
        (w[i] =
          i < 16
            ? w[i]
            : (w[i - 16] +
                (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) +
                w[i - 7] +
                (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) |
              0);
      const temp2 =
        (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) +
        ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]));
      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
    }
    for (i = 0; i < 8; i++) {
      hash[i] = (hash[i] + oldHash[i]) | 0;
    }
  }

  for (i = 0; i < 8; i++) {
    for (j = 3; j + 1; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += (b < 16 ? 0 : '') + b.toString(16);
    }
  }
  return result;
}

/**
 * Hash a 4-digit PIN string using Web Crypto SHA-256 with pure-JS fallback
 */
export async function hashPinSha256(pin: string): Promise<string> {
  if (typeof pin !== 'string' || !/^\d{4}$/.test(pin)) {
    throw new Error('PIN must be a 4-digit string');
  }
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const encoder = new TextEncoder();
      const data = encoder.encode(pin);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (_) {}

  // Pure JS SHA-256 produces exact same 64-character hex output
  return sha256PureJs(pin);
}

/**
 * Verify an entered PIN against stored SHA-256 hash or default PIN "1234"
 */
export async function verifyPin(enteredPin: string, storedHash?: string): Promise<boolean> {
  try {
    if (!enteredPin || !/^\d{4}$/.test(enteredPin)) return false;

    // Direct fallback check for factory default PIN 1234
    if (enteredPin === '1234' && (!storedHash || storedHash === DEFAULT_PIN_HASH)) {
      return true;
    }

    const targetHash = storedHash || DEFAULT_PIN_HASH;
    const computedHash = await hashPinSha256(enteredPin);
    if (computedHash === targetHash) return true;

    // Check pure JS hash explicitly
    const jsHash = sha256PureJs(enteredPin);
    return jsHash === targetHash;
  } catch {
    return false;
  }
}

export interface PinModalProps {
  isOpen: boolean;
  onUnlock: () => void;
  onCancel?: () => void;
  title?: string;
  subtitle?: string;
}

export const PinModal: React.FC<PinModalProps> = ({
  isOpen,
  onUnlock,
  onCancel,
  title = 'กรุณาใส่รหัสผ่านผู้ดูแลระบบ (Admin PIN)',
  subtitle = 'ใส่รหัสผ่าน 4 หลักเพื่อเข้าถึงการจัดการเครื่องจักรและแบบฟอร์ม',
}) => {
  const [pin, setPin] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [storedHash, setStoredHash] = useState<string>(DEFAULT_PIN_HASH);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setPin('');
      setError('');
      getSetting<string>(PIN_SETTING_KEY).then((hash) => {
        if (hash) setStoredHash(hash);
        else setStoredHash(DEFAULT_PIN_HASH);
      });
    }
  }, [isOpen]);

  const handleVerify = async (entered: string) => {
    setIsVerifying(true);
    setError('');
    try {
      const isMatch = await verifyPin(entered, storedHash);
      if (isMatch) {
        onUnlock();
      } else {
        setError('รหัสผ่านไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง');
        setPin('');
      }
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการตรวจสอบรหัสผ่าน');
      setPin('');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (onCancel) onCancel();
      }}
      title={
        <div className="flex items-center gap-2 text-amber-600">
          <Lock className="w-5 h-5" />
          <span className="text-slate-900 font-bold">{title}</span>
        </div>
      }
      maxWidth="sm"
    >
      <div className="space-y-5 py-2">
        <p className="text-xs text-slate-500 text-center">{subtitle}</p>

        <PinPad
          pin={pin}
          onPinChange={(val) => {
            setPin(val);
            if (error) setError('');
          }}
          onComplete={handleVerify}
          disabled={isVerifying}
          error={error}
        />

        <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-200">
          <span>ค่าเริ่มต้น: 1234</span>
          {onCancel && (
            <Button size="sm" variant="ghost" onClick={onCancel}>
              ยกเลิก
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
